// Whether a PinGolf night is currently taking card sales.
//
// This mirrors isPingolfSalesOpen in server/src/services/eventPingolfService.js. The server is
// the enforcer; this exists so the UI shows the same answer rather than offering a button the
// API will refuse. Any change to the rule has to land in both, which is why it is one shared
// module here instead of the hand-copied expressions it replaces.
//
// The host's toggle is authoritative in both directions once set - true forces open, false
// forces closed - and the clock is only the default for a night nobody has touched. Treating
// the clock as an override that could only force *open* is what let a host close sales
// mid-night and have players keep buying until the scheduled end time.

const lifecycleOf = (event) => event?.event_metadata?.pingolf?.lifecycle || {};

function getPingolfEventWindow(event) {
  const startMs = event?.event_date ? new Date(event.event_date).getTime() : NaN;
  if (Number.isNaN(startMs)) return null;

  const durationMinutes = Number(event.duration_minutes) || 240;
  return { startMs, endMs: startMs + durationMinutes * 60 * 1000 };
}

function isPingolfEventActiveNow(event) {
  const window = getPingolfEventWindow(event);
  if (!window) return false;

  const now = Date.now();
  return now >= window.startMs && now <= window.endMs;
}

function isPingolfSalesOpen(event) {
  const lifecycle = lifecycleOf(event);
  if (typeof lifecycle.sales_open === 'boolean') {
    return lifecycle.sales_open;
  }

  return isPingolfEventActiveNow(event);
}

// WHY sales are shut, for a UI that has to explain itself. "Not open yet" and "closed" are not
// distinguishable from the clock alone: a host can close sales early (before the scheduled
// start) or open them late, so a naive "is the start time in the future?" test tells a player
// waiting on a host-closed night that sales open when the night starts, which is wrong and
// unactionable. The toggle is the same authority here that it is in isPingolfSalesOpen above -
// an explicit false is a deliberate close whenever it happened, and only an untouched night
// falls back to the clock.
export function getPingolfSalesState(event) {
  if (isPingolfSalesOpen(event)) return 'open';

  if (lifecycleOf(event).sales_open === false) return 'closed_by_host';

  const window = getPingolfEventWindow(event);
  if (window && Date.now() < window.startMs) return 'not_open_yet';

  // Includes an event with no usable date: nothing to promise a player about, so the honest
  // answer is the closed one rather than a "they open at ..." that names no time.
  return 'night_over';
}

export function canPurchasePingolfCards(event) {
  if (!event) return false;

  const pingolf = event.event_metadata?.pingolf || {};
  return !pingolf.finalized_at && !pingolf.canceled_at && isPingolfSalesOpen(event);
}
